import React, { useEffect, useState } from 'react';
import { FiArrowRight, FiBriefcase, FiCheckCircle, FiFileText, FiPackage, FiShoppingCart, FiUsers, FiBarChart2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { USERNAME, PASSWORD } from '../../config';
import DashboardSection from '../../Component/common/Dashboard/DashboardSection';
import DateFilter from '../../Component/common/datefilter/DateFilter';
import { getTodos } from '../../api/todo';
import { getLeadsWithAuth } from '../../api/leads';
import { getContacts } from '../../api/contacts';
import { getInvoices } from '../../api/invoice';
import './dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  //leads, contacts,tasks and invoces state management
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [errorLeads, setErrorLeads] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [errorContacts, setErrorContacts] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [completedTasksCount, setCompletedTasksCount] = useState(0);
  const [futureTasksCount, setFutureTasksCount] = useState(0);
  const [totalTasksCount, setTotalTasksCount] = useState(0);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [errorTasks, setErrorTasks] = useState('');
  // Invoice state management
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingLeads(true);
      setLoadingContacts(true);
      setErrorLeads('');
      setErrorContacts('');
      setLoadingTasks(true);
      setErrorTasks('');

      try {
        const [leadsResponse, contactsResponse, todosResponse, invoicesResponse] = await Promise.all([
          getLeadsWithAuth(USERNAME, PASSWORD, { fields: 'id,vc_enquiry_id,posted_on,responded,created_at' }),
          getContacts(USERNAME, PASSWORD),
          getTodos(),
          getInvoices(USERNAME, PASSWORD)
        ]);

        console.log('leadsResponse.data:', leadsResponse.data);
        console.log('contactsResponse.data:', contactsResponse.data);
        console.log('todosResponse.data:', todosResponse.data);
        console.log('invoicesResponse.data:', invoicesResponse.data);

        const leadsData = Array.isArray(leadsResponse.data) ? leadsResponse.data : leadsResponse.data?.leads || [];
        const contactsData = Array.isArray(contactsResponse.data) ? contactsResponse.data : contactsResponse.data?.contacts || [];
        const todosData = Array.isArray(todosResponse.data) ? todosResponse.data : todosResponse.data?.todos || [];
        const invoicesData = Array.isArray(invoicesResponse.data) ? invoicesResponse.data : invoicesResponse.data?.todos || [];

        setLeads(leadsData.map(lead => ({
          ...lead,
          posted_on: lead.posted_on || lead.created_at
        })));

        setContacts(contactsData);

        const pendingTasks = (() => {
          const now = new Date();
          return todosData.filter(todo => {
            if (todo.status !== 'pending') return false;
            if (!todo.targetDate) return true; // If no targetDate, consider as pending
            const dueDate = new Date(todo.targetDate);
            return dueDate <= now;
          });
        })();
        setPendingTasksCount(pendingTasks.length);

        const completedTasks = todosData.filter(todo => todo.status === 'completed');
        setCompletedTasksCount(completedTasks.length);

        const now = new Date();
        const futureTasks = todosData.filter(todo => {
          if (!todo.targetDate) return false;
          const dueDate = new Date(todo.targetDate);
          return dueDate > now && todo.status === 'pending';
        });
        setFutureTasksCount(futureTasks.length);

        const totalTasks = todosData.filter(todo => todo.status !== 'completed');
        setTotalTasksCount(totalTasks.length);
        console.log('Total Tasks:', totalTasks.length);
        console.log('Total Task IDs:', totalTasks.map(task => task.id));

        console.log('Pending Task IDs:', pendingTasks.map(task => task.id));
        console.log('Future Task IDs:', futureTasks.map(task => task.id));

        setInvoices(invoicesData);


      } catch (err) {
        setErrorLeads('Failed to fetch leads');
        setErrorContacts('Failed to fetch contacts');
        setErrorTasks('Failed to fetch tasks');
        console.error('API Error:', err);
      } finally {
        setLoadingLeads(false);
        setLoadingContacts(false);
        setLoadingTasks(false);
      }
    };

    fetchData();
  }, []);

  const filterByDateRange = (items, dateField = 'posted_on') => {
    if (!fromDate && !toDate) return items;

    const startDate = fromDate ? new Date(fromDate) : new Date(0);
    startDate.setHours(0, 0, 0, 0);

    const endDate = toDate ? new Date(toDate) : new Date();
    endDate.setHours(23, 59, 59, 999);

    return items.filter(item => {
      const rawDate = item[dateField] || item.created_at || item.createdAt || item.posted_on || item.invoice_date || item.invoiceDate;
      if (!rawDate) return false;
      
      try {
        const itemDate = new Date(rawDate);
        if (isNaN(itemDate.getTime())) return false;
        
        return itemDate >= startDate && itemDate <= endDate;
      } catch (e) {
        console.warn('Invalid date format for item:', item.id, item[dateField]);
        return false;
      }
    });
  };

  // Apply filters using the posted_on field (fallback to created_at)
  const filteredLeads = filterByDateRange(leads);
  const filteredContacts = filterByDateRange(contacts);
  const filteredInvoices = filterByDateRange(invoices, 'invoice_date');


  // Calculate lead metrics
  const totalLeads = filteredLeads.length;
  const statusCounts = filteredLeads.reduce((acc, lead) => {
    const status = lead.responded || 'New';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Calculate contact metrics
  const totalContacts = filteredContacts.length;
  const manufacturerCount = filteredContacts.filter(c => c.contact_type === 'manufacturer').length;
  const buyerCount = filteredContacts.filter(c => c.contact_type === 'buyer').length;
  const contractorCount = filteredContacts.filter(c => c.contact_type === 'contractor').length;

    // Calculate Invoice metrics
  const totalInvoices = filteredInvoices.length;

  const setPresetRange = (preset) => {
    const today = new Date();
    const start = new Date(today);
    if (preset === '7d') start.setDate(today.getDate() - 6);
    if (preset === '30d') start.setDate(today.getDate() - 29);
    if (preset === '90d') start.setDate(today.getDate() - 89);
    if (preset === 'month') start.setDate(1);
    if (preset === 'all') { setFromDate(''); setToDate(''); }
    else { setFromDate(start.toISOString().slice(0, 10)); setToDate(today.toISOString().slice(0, 10)); }
    setDatePreset(preset);
  };

  const leadTrend = Array.from({ length: 6 }, (_, index) => {
    const monthDate = new Date(); monthDate.setDate(1); monthDate.setMonth(monthDate.getMonth() - (5 - index));
    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
    const count = filteredLeads.filter(lead => {
      const date = new Date(lead.posted_on || lead.created_at);
      return !Number.isNaN(date.getTime()) && date >= start && date < end;
    }).length;
    return { label: monthDate.toLocaleDateString('en-IN', { month: 'short' }), count };
  });
  const maxTrend = Math.max(1, ...leadTrend.map(item => item.count));
  const statusChart = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const statusTotal = statusChart.reduce((sum, item) => sum + item[1], 0) || 1;

  return (
    <div className="dashboard-container">
      <div className="dashboard-hero">
        <div>
          <span className="dashboard-eyebrow">CRM OVERVIEW</span>
          <h2>Dashboard</h2>
          <p>Track leads, customers, tasks and store activity from one place.</p>
        </div>
        <div className="dashboard-quick-actions">
          <button onClick={() => navigate('/buy-leads')}><FiBriefcase /> New Lead</button>
          <button onClick={() => navigate('/invoice')}><FiFileText /> Create Invoice</button>
        </div>
      </div>

      <div className="dashboard-kpi-grid">
        <button className="dashboard-kpi" onClick={() => navigate('/buy-leads')}><span className="kpi-icon leads"><FiBriefcase /></span><span><small>Total Leads</small><strong>{totalLeads}</strong></span><FiArrowRight /></button>
        <button className="dashboard-kpi" onClick={() => navigate('/contacts')}><span className="kpi-icon contacts"><FiUsers /></span><span><small>Total Contacts</small><strong>{totalContacts}</strong></span><FiArrowRight /></button>
        <button className="dashboard-kpi" onClick={() => navigate('/todo')}><span className="kpi-icon tasks"><FiCheckCircle /></span><span><small>Open Tasks</small><strong>{pendingTasksCount}</strong></span><FiArrowRight /></button>
        <button className="dashboard-kpi" onClick={() => navigate('/invoice')}><span className="kpi-icon invoices"><FiFileText /></span><span><small>Total Invoices</small><strong>{totalInvoices}</strong></span><FiArrowRight /></button>
      </div>

      <div className="dashboard-filter-card">
        <div className="dashboard-filter-heading"><strong>Report filters</strong><span>Choose a period to update reporting metrics</span></div>
        <div className="dashboard-filter-controls">
          <div className="dashboard-presets" aria-label="Date range presets">
            {[['7d', '7 days'], ['30d', '30 days'], ['90d', '90 days'], ['month', 'This month'], ['all', 'All time']].map(([value, label]) => <button key={value} type="button" className={datePreset === value ? 'active' : ''} onClick={() => setPresetRange(value)}>{label}</button>)}
          </div>
          <DateFilter fromDate={fromDate} toDate={toDate} setFromDate={value => { setFromDate(value); setDatePreset('custom'); }} setToDate={value => { setToDate(value); setDatePreset('custom'); }} />
        </div>
      </div>


      <section className="dashboard-reporting">
        <div className="reporting-heading">
          <div><span className="dashboard-eyebrow">AT A GLANCE</span><h3>Business performance</h3><p>Trends and lead status for the selected reporting period.</p></div>
        </div>
        <div className="dashboard-chart-grid">
          <article className="dashboard-chart-card">
            <div className="chart-card-heading"><div><h4>Lead activity</h4><p>Lead records created over the last six months</p></div><FiBarChart2 /></div>
            <div className="lead-trend-chart" role="img" aria-label="Lead volume for the last six months">
              {leadTrend.map(item => <div className="trend-column" key={item.label}><span className="trend-value">{item.count}</span><div className="trend-bar-track"><div className="trend-bar" style={{ height: Math.max(item.count > 0 ? 8 : 2, (item.count / maxTrend) * 100) + '%' }} /></div><span className="trend-label">{item.label}</span></div>)}
            </div>
          </article>
          <article className="dashboard-chart-card">
            <div className="chart-card-heading"><div><h4>Lead status</h4><p>Top statuses in the selected period</p></div><FiBriefcase /></div>
            {statusChart.length ? <div className="status-chart-list">{statusChart.map(([status, count], index) => <div className="status-chart-row" key={status}><div className="status-chart-label"><span className={'status-dot status-color-' + index} /><span>{status}</span><strong>{count}</strong></div><div className="status-track"><div className={'status-fill status-color-' + index} style={{ width: (count / statusTotal) * 100 + '%' }} /></div></div>)}</div> : <p className="chart-empty">No lead status data for this period.</p>}
          </article>
        </div>
      </section>

      <section className="dashboard-store-panel">
        <div>
          <span className="store-panel-icon"><FiPackage /></span>
          <div><h3>Store</h3><p>Manage inventory, invoices and purchase orders.</p></div>
        </div>
        <div className="store-panel-actions">
          <button onClick={() => navigate('/store/stock')}><FiPackage /> Stock Details</button>
          <button onClick={() => navigate('/store/invoices')}><FiFileText /> Invoices</button>
          <button onClick={() => navigate('/store/purchase-orders')}><FiShoppingCart /> Purchase Orders</button>
        </div>
      </section>

    </div>
  );};

export default Dashboard;
